import React, { forwardRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { BaseBlockProps, ChartDataItem } from '../types';
import { useUserMostActiveStatistics, UserMostActiveData } from '../../../hooks';
import { getSchemeOrDefault } from '../../../utils/chartConfig';
import {
    BlockWrapper,
    BlockHeader,
    BlockToolbar,
    LoadingState,
} from '../base';
import { PieChart } from '../../../Components/Display';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface UserMostActiveStatisticsBlockProps extends BaseBlockProps {}

export const UserMostActiveStatisticsBlock = forwardRef<HTMLDivElement, UserMostActiveStatisticsBlockProps>(
    ({ kedo, block, dashboard, onClose, onMax, onSuccess }, ref) => {
        const { data, loading, refetch } = useUserMostActiveStatistics(kedo, block);

        const chartData = useMemo((): ChartDataItem[] => {
            if (!data || !Array.isArray(data)) return [];

            return data.map((item: UserMostActiveData) => ({
                id: item.username ?? String(item.user_id),
                label: item.username ?? String(item.user_id),
                value: item.total_count,
            }));
        }, [data]);

        const scheme = getSchemeOrDefault(block.settings?.scheme);

        return (
            <BlockWrapper ref={ref} dashboard={dashboard}>
                <BlockHeader>
                    <BlockToolbar
                        kedo={kedo}
                        block={block}
                        onClose={onClose}
                        onMax={onMax}
                        onSuccess={onSuccess}
                        fetchData={refetch}
                    />
                </BlockHeader>
                <Typography variant="h6" component="h3" gutterBottom>
                    {kedo.t('User most active')}
                </Typography>
                <Box sx={{ height: 300, width: '100%' }}>
                    {loading ? (
                        <LoadingState />
                    ) : (
                        <PieChart
                            data={chartData}
                            colors={{ scheme }}
                            innerRadius={0.75}
                            enableArcLinkLabels={true}
                            enableArcLabels={true}
                            legendPosition="bottom"
                        />
                    )}
                </Box>
            </BlockWrapper>
        );
    }
);

UserMostActiveStatisticsBlock.displayName = 'UserMostActiveStatisticsBlock';
